import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Bell, BellOff, ArrowRight, Package } from "lucide-react";

/**
 * Buyer stock-watch ledger. Lists every pending "notify me when back" row
 * tied to this account (either by user_id or matching email). Rows auto-
 * expire after 90 days on the backend, so anything shown here is still live.
 */
export default function AccountWatchlist() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [removing, setRemoving] = useState(null);

  const load = () => {
    api.get("/stock-watch")
      .then((r) => setRows(r.data || []))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  };
  useEffect(load, []);

  const remove = async (id) => {
    setRemoving(id);
    try {
      await api.delete(`/stock-watch/${encodeURIComponent(id)}`);
      setRows((prev) => (prev || []).filter((r) => r.id !== id));
      toast.success("Removed from your watchlist.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove — try again.");
    } finally {
      setRemoving(null);
    }
  };

  if (err) return <div className="text-[var(--crimson)]" data-testid="watchlist-error">Failed to load: {err}</div>;
  if (!rows) return <div className="text-sm font-mono uppercase tracking-wider text-[var(--ink-muted)]">Loading watchlist…</div>;

  return (
    <div className="space-y-6" data-testid="account-watchlist-page">
      <div>
        <div className="eyebrow flex items-center gap-2"><Bell className="h-3.5 w-3.5" /> Stock watchlist</div>
        <h2 className="font-display text-3xl md:text-4xl mt-1 leading-[1.05]">
          {rows.length} {rows.length === 1 ? "product" : "products"} watched
        </h2>
        <p className="text-sm text-[var(--ink-soft)] mt-2 max-w-2xl leading-relaxed">
          We email you the moment any of these variants ships back to the warehouse.
          Rows auto-expire after 90 days — resubscribe from the product page if you still need it.
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-[var(--line)] rounded-md p-8 text-center text-[var(--ink-soft)]" data-testid="watchlist-empty">
          <Package className="h-8 w-8 mx-auto text-[var(--ink-muted)]" strokeWidth={1.4} />
          <div className="mt-3 font-display text-lg text-[var(--ink)]">Nothing on your watchlist.</div>
          <div className="text-sm mt-1">Tap <span className="font-mono">Notify me when back</span> on any out-of-stock variant to add it here.</div>
          <Link to="/shop" className="btn-primary inline-flex mt-4" data-testid="watchlist-empty-shop">
            Browse catalog <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <div className="border border-[var(--line)] rounded-md overflow-hidden bg-[var(--paper-2)]">
          <table className="w-full text-sm" data-testid="watchlist-table">
            <thead className="bg-[var(--paper-3)] text-[10.5px] uppercase tracking-[0.16em] text-[var(--ink-muted)] font-mono">
              <tr>
                <th className="text-left px-4 py-2.5">Product</th>
                <th className="text-left px-4 py-2.5">Variant</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Watching since</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--line)]">
              {rows.map((w) => (
                <tr key={w.id} className="hover:bg-[var(--paper-3)]" data-testid={`watchlist-row-${w.id}`}>
                  <td className="px-4 py-3">
                    <Link
                      to={`/shop/${w.sku}${w.variant ? `?variant=${encodeURIComponent(w.variant)}` : ""}`}
                      className="hover:text-[var(--terra)] underline-offset-2 hover:underline"
                    >
                      {w.product_name || w.sku}
                    </Link>
                    <div className="font-mono text-[10.5px] text-[var(--ink-muted)] mt-0.5">{w.sku}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-[12.5px]">{w.variant || "—"}</td>
                  <td className="px-4 py-3 text-[12.5px] text-[var(--ink-soft)] hidden md:table-cell font-mono">
                    {new Date(w.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" })}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => remove(w.id)}
                      disabled={removing === w.id}
                      className="inline-flex items-center gap-1.5 text-[11.5px] font-mono uppercase tracking-[0.14em] text-[var(--rust)] hover:underline disabled:opacity-50"
                      data-testid={`watchlist-remove-${w.id}`}
                    >
                      <BellOff className="h-3.5 w-3.5" /> {removing === w.id ? "Removing…" : "Stop watching"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
